import { FlagIcon, WriteIcon } from "@xola/icons";
import { Dot } from "@xola/ui-kit";
import { getCustomerName } from "common";
import React, { useEffect, useState } from "react";
import { ExperiencePurchaseItem, Guest, Permission, Purchase } from "types";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { NoTranslate } from "../../Common/NoTranslate";
import { CheckInPurchaseGuestListSkeleton } from "./CheckInPurchaseGuestListSkeleton";
import { CheckInPurchaseGuestMenu } from "./CheckInPurchaseGuestMenu";

interface CheckInPurchaseGuestListProps {
    purchase: Purchase;
    purchaseItem: ExperiencePurchaseItem;
    onSubMenuOpenChange: (value: boolean) => void;
}

export const CheckInPurchaseGuestList = ({
    purchase,
    purchaseItem,
    onSubMenuOpenChange,
}: CheckInPurchaseGuestListProps) => {
    /**
     * In cases when we have a lot of guests 30-40+ (and probably a weaker machine) the list can load for a second or two,
     * so we need to give some feedback to the user to indicate the app is not stuck.
     */
    const [isRenderingGuestList, setIsRenderingGuestList] = useState(true);
    useEffect(() => setIsRenderingGuestList(false), []);

    if (isRenderingGuestList) {
        return <CheckInPurchaseGuestListSkeleton />;
    }

    return (
        <div>
            <div className="flex items-center justify-between rounded-t-lg bg-gray-lighter p-4">
                <div className="text-left">
                    <p className="text-sm font-bold leading-p1 text-warning">
                        <FlagIcon /> Group
                    </p>
                    <NoTranslate as="h3" className="break-word leading-p1">
                        {getCustomerName(purchase)}
                    </NoTranslate>
                </div>
                <div className="flex-shrink-0">
                    <CheckInPurchaseGuestMenu
                        isOrganizer
                        purchase={purchase}
                        purchaseItem={purchaseItem}
                        onOpenChange={onSubMenuOpenChange}
                    />
                </div>
            </div>
            <div className="h-80 divide-y divide-gray-lighter overflow-y-auto">
                {purchaseItem.guests.map((guest, index) => (
                    <PurchaseGuest
                        key={guest.id}
                        guest={guest}
                        index={index}
                        purchase={purchase}
                        purchaseItem={purchaseItem}
                        onSubMenuOpenChange={onSubMenuOpenChange}
                    />
                ))}
            </div>
        </div>
    );
};

interface GuestListProps extends CheckInPurchaseGuestListProps {
    guest: Guest;
    index: number;
}

const PurchaseGuest = ({ guest, index, purchase, purchaseItem, onSubMenuOpenChange }: GuestListProps) => {
    const { isGranted } = useAuthorization();
    const showWaiver = isGranted(Permission.WAIVERS_VIEW);
    const linkedWaiverParticipant = showWaiver ? !!guest.waiver?.id && !!guest.participant : null;

    return (
        <div key={guest.id} className="flex items-center justify-between p-4 py-2">
            <div className="flex items-center space-x-2">
                {guest.primary ? <FlagIcon /> : <div className="ml-3.5" />}
                {guest.name ? (
                    <p className="no-translate leading-p1 tracking-tightest">{guest.name}</p>
                ) : (
                    <p className="leading-p1 tracking-tightest">
                        Guest <var data-var="number">#{index + 1}</var>
                    </p>
                )}
            </div>
            <div className="flex items-center space-x-2">
                <span className="flex items-center">
                    <Dot size="small" color={linkedWaiverParticipant ? "success" : "danger"} />
                    <WriteIcon />
                </span>
                <CheckInPurchaseGuestMenu
                    purchase={purchase}
                    purchaseItem={purchaseItem}
                    guest={guest}
                    onOpenChange={onSubMenuOpenChange}
                />
            </div>
        </div>
    );
};
